// import { useSession } from "next-auth/react";

import { SetStateAction, useMemo, useState } from "react";
import {
  StyledBoldHeaderTitle,
  StyledHeader,
  StyledModal,
  StyledListBodyDiv,
  StyledCellExplanationContainer,
  PaymentTextWrapper,
  PlanSegmented,
  StyledSwitchButton,
} from "./Style";
import { MODAL_NAME } from "./constants";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { projectActions } from "@/redux/stores/projectStore";
import { getProjectState } from "@/redux/store";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useGetPlansQuery } from "@/redux/api/paymentApi";
import {
  TeamCell,
  EnterpriseCell,
  ExplanationLine,
  PersonalCell,
  PlanType,
  FreeCell,
} from "./Cell";
import { useGetUserInfoQuery } from "@/redux/api/userApi";
import { TeamPlusButton } from "./Button/TeamPlusButton";

interface ModalHeaderProps {
  period: boolean;
  setPeriod: React.Dispatch<SetStateAction<boolean>>;
  changeToApiPlan: () => void;
}
const ModalHeader = ({ period, setPeriod, changeToApiPlan }: ModalHeaderProps) => {
  const { t } = useTranslation("common");
  const onClickSwitch = () => {
    setPeriod(!period);
  };

  const options = [
    {
      label: (
        <div className="button-wrapper" onClick={onClickSwitch}>
          <span>{t("Monthly")}</span>
        </div>
      ),
      value: true,
    },
    {
      label: (
        <div className="button-wrapper" onClick={onClickSwitch}>
          <span>{t("Yearly")}</span>
          <span className="discount">-20%</span>
        </div>
      ),
      value: false,
    },
  ];

  return (
    <StyledHeader>
      <StyledBoldHeaderTitle>{t("Choose a plan")}</StyledBoldHeaderTitle>
      <PlanSegmented value={period} options={options} />
      {/* 
        TODO: API 플랜 리스트로 교체 버튼은 임시로 숨김\
        TODO: API 플랜이 확정되면 다시 부활 예정
      */}
      {/* <StyledSwitchButton onClick={() => changeToApiPlan()}>
        {t("Looking for API Plans?")}
      </StyledSwitchButton> */}
    </StyledHeader>
  );
};

interface PriceAndSliderProps {
  period?: boolean;
  plan?: string;
  plans?: PlanType[];
  seats?: number;
  setSeats?: (count: number) => void;
  subscriptionType: string;
  // setUserSelected: React.Dispatch<SetStateAction<boolean>>;
}

export const PriceAndSlider = (props: PriceAndSliderProps) => {
  const { t } = useTranslation("common");
  const {
    period = true,
    plan = "personal",
    plans,
    seats = 1,
    setSeats,
    subscriptionType,
  } = props;

  const product = useMemo(() => {
    return plans?.find(
      (e: any) =>
        e.mark.includes("unlimit") && e.planName.toLowerCase().includes(plan)
    );
  }, [plan, plans]);
  const price = period
    ? product?.pricePerMonth
    : product?.pricePerMonthByYearly;

  const annulPrice = price * 12;

  const priceText = useMemo(() => {
    if (plan === "free") {
      return t("No Credit card required");
    }
    if (!period) {
      if (plan === "personal") {
        return t(`$${annulPrice} Billed Annually`);
      }
      if (plan === "team") {
        return t(`$${annulPrice} per seat Billed Annually`);
      }
    }
    return t("Billed Monthly");
  }, [annulPrice, period, plan, t]);

  return (
    <PaymentTextWrapper>
      <div className="text-space">
        <div className="price-info">
          <span className="price">{`$ ${+price || 0}`}</span>
          <span className="period"> /{t("Mo")}</span>
          {plan?.includes("team") && (
            <span className="seat"> /{t("seat")}</span>
          )}
        </div>
        <div className="annuallyText">{priceText}</div>
      </div>
      {plan === "team" ? (
        <div className="team-button-wrapper">
          <TeamPlusButton
            seats={seats}
            setSeats={setSeats}
            subscriptionType={subscriptionType}
          />
        </div>
      ) : (
        <div style={{ height: "38px" }}></div>
      )}
    </PaymentTextWrapper>
  );
};

export interface SubscriptionCellDetailsProps {
  plan: string;
  plans: any;
  period: boolean;
}

export const SubscriptionCellDetails = ({
  plan,
  plans,
  period,
}: SubscriptionCellDetailsProps) => {
  // const products = plan === "starter" ? starterProducts : creatorProducts;
  const { t } = useTranslation("common");
  const date = period ? "monthly" : "yearly";
  const product = useMemo(() => {
    return plans?.find(
      (e: any) =>
        e.mark.includes("unlimit") && e.planName.toLowerCase().includes(plan)
    );
  }, [plan, plans]);

  const personalPlanStrings = useMemo(() => {
    return [
      {
        key: "personalTitle1",
        subTitle: t("What's included"),
        frontText: "",
        boldText: "",
        backText: "",
      },
      {
        key: "personalText1",
        subTitle: "",
        frontText: "",
        boldText: "",
        backText: "",
        blueBoldText: t("Unlimited Video Exports"),
      },
      {
        key: "personalText2",
        subTitle: "",
        frontText: "",
        boldText: "",
        backText: "",
        blueBoldText: t("Videos up to 30 mins"),
      },
      {
        key: "personalText3",
        subTitle: "",
        frontText: "",
        boldText: "",
        backText: "",
        blueBoldText: t("{{avatars}} Custom Avatars", {
          avatars: 3,
        }),
      },
      {
        key: "personalText4",
        subTitle: "",
        frontText: "",
        boldText: "",
        backText: "",
        blueBoldText: t("{{credits}} Generative Credits", {
          credits: period ? 60 : 720,
        }),
      },
      {
        key: "personalText5",
        subTitle: "",
        frontText: "",
        boldText: "",
        backText: "",
        blueBoldText: t("{{resolution}} Video Export", {
          resolution: "1080p",
        }),
      },
      {
        key: "personalText6",
        subTitle: "",
        frontText: "",
        boldText: "",
        backText: "",
        blueBoldText: t("Fast Video Processing"),
      },
      
      {
        key: "personalTitle2",
        subTitle: t("Everything in Free"),
        frontText: "",
        boldText: "",
        backText: "",
      },
      {
        key: "personalText7",
        subTitle: "",
        frontText: t("{{avatars}} Photo Avatars", {
          avatars: "Unlimited",
        }),
        boldText: "",
        backText: "",
      },
      {
        key: "personalText8",
        subTitle: "",
        frontText: t("Unlimited Youtube Downloader"),
        boldText: "",
        backText: "",
      },
      {
        key: "personalText9",
        subTitle: "",
        frontText: t("AI Dubbing with SRT/ASS Upload"),
        boldText: "",
        backText: "",
      },
      {
        key: "personalText10",
        subTitle: "",
        frontText: t("Voice Cloning with Audio Upload"),
        boldText: "",
        backText: "",
      },
      {
        key: "personalText11",
        subTitle: "",
        frontText: t("Generate Videos from Any Input"),
        boldText: "",
        backText: "",
      },
      {
        key: "personalText12",
        subTitle: "",
        frontText: t("{{languages}} Languages, {{voices}} Voices", {
          languages: "110+",
          voices: "1000+",
        }),
        boldText: "",
        backText: "",
      },
      {
        key: "personalText13",
        subTitle: "",
        frontText: t("{{templates}} Video Templates", {
          templates: "7,000+",
        }),
        boldText: "",
        backText: "",
      },
      {
        key: "personalText14",
        subTitle: "",
        frontText: t("{{media}} Premium Shutterstock Media", {
          media: "3M+",
        }),
        boldText: "",
        backText: "",
      },
      {
        key: "personalText15",
        subTitle: "",
        frontText: t("Supports Add-ons"),
        boldText: "",
        backText: "",
      },
      {
        key: "personalText16",
        subTitle: "",
        frontText: t("No Watermark"),
        boldText: "",
        backText: "",
      },
    ];
  }, [t, period]);

  const teamPlanStrings = useMemo(() => {
    return [
      {
        key: "teamTitle1",
        subTitle: t("What's included"),
        frontText: "",
        boldText: "",
        backText: "",
      },
      {
        key: "teamText1",
        subTitle: "",
        frontText: "",
        boldText: "",
        backText: "",
        blueBoldText: t("Unlimited Video Exports"),
      },
      {
        key: "teamText2",
        subTitle: "",
        frontText: "",
        boldText: "",
        backText: "",
        blueBoldText: t("Videos up to 30 mins"),
      },
      {
        key: "teamText3",
        subTitle: "",
        frontText: "",
        boldText: "",
        backText: "",
        blueBoldText: t("{{avatars}} Custom Avatars", {
          avatars: 5,
        }),
      },
      {
        key: "teamText4",
        subTitle: "",
        frontText: "",
        boldText: "",
        backText: "",
        blueBoldText: t("{{credits}} Generative Credits", {
          credits: period ? 150 : 1800,
        }),
      },
      {
        key: "teamText5",
        subTitle: "",
        frontText: "",
        boldText: "",
        backText: "",
        blueBoldText: t("{{resolution}} Video Export", {
          resolution: "4K",
        }),
      },
      {
        key: "teamText6",
        subTitle: "",
        frontText: "",
        boldText: "",
        backText: "",
        blueBoldText: t("Faster Video Processing"),
      },

      {
        key: "teamTitle2",
        subTitle: t("Everything in Personal"),
        frontText: "",
        boldText: "",
        backText: "",
      },
      {
        key: "teamText7",
        subTitle: "",
        frontText: t("Proofread AI Dubbing Scripts"),
        boldText: "",
        backText: "",
      },
      {
        key: "teamText8",
        subTitle: "",
        frontText: t("Up/Download Subtitle Files"),
        boldText: "",
        backText: "",
      },
      {
        key: "teamText9",
        subTitle: "",
        frontText: t("One-Click Translation"),
        boldText: "",
        backText: "",
      },
      {
        key: "teamText10",
        subTitle: "",
        frontText: t("Premium {{avatars}} AI Avatars", {
          avatars: "125+",
        }),
        boldText: "",
        backText: "",
      },
      {
        key: "teamText11",
        subTitle: "",
        frontText: t("{{resolution}} Youtube Downloader", {
          resolution: "4K",
        }),
        boldText: "",
        backText: "",
      },
      {
        key: "teamText12",
        subTitle: "",
        frontText: t("Avatar Gesture Control"),
        boldText: "",
        backText: "",
      },
      {
        key: "teamText13",
        subTitle: "",
        frontText: t("Multi-Avatar Scenes"),
        boldText: "",
        backText: "",
      },
      {
        key: "teamText14",
        subTitle: "",
        frontText: t("Effect Animation with Timing"),
        boldText: "",
        backText: "",
      },
      {
        key: "teamText15",
        subTitle: "",
        frontText: t("Team Collaboration Workspace"),
        boldText: "",
        backText: "",
      },
      {
        key: "teamText16",
        subTitle: "",
        frontText: t("Custom Brand Kit"),
        boldText: "",
        backText: "",
      },
    ];
  }, [t, period]);

  const enterprisePlanStrings = useMemo(() => {
    return [
      {
        key: "enterpriseTitle1",
        subTitle: t("What's included"),
        frontText: "",
        boldText: "",
        backText: "",
      },
      {
        key: "enterpriseText1",
        subTitle: "",
        frontText: "",
        boldText: "",
        backText: "",
        blueBoldText: t("Unlimited Video Exports"),
      },
      {
        key: "enterpriseText2",
        subTitle: "",
        frontText: "",
        boldText: "",
        backText: "",
        blueBoldText: t("Unlimited Video Duration"),
      },
      {
        key: "enterpriseTex3",
        subTitle: "",
        frontText: "",
        boldText: "",
        backText: "",
        blueBoldText: t("Unlimited Custom Avatars"),
      },
      {
        key: "enterpriseText4",
        subTitle: "",
        frontText: "",
        boldText: "",
        backText: "",
        blueBoldText: t("Flexible Generative Credits"),
      },
      {
        key: "enterpriseText5",
        subTitle: "",
        frontText: "",
        boldText: "",
        backText: "",
        blueBoldText: t("{{resolution}} Video Export", {
          resolution: "4K",
        }),
      },
      {
        key: "enterpriseText6",
        subTitle: "",
        frontText: "",
        boldText: "",
        backText: "",
        blueBoldText: t("Priority Video Processing"),
      },

      {
        key: "enterpriseTitle2",
        subTitle: t("Everything in Team"),
        frontText: "",
        boldText: "",
        backText: "",
      },
      {
        key: "enterpriseText7",
        subTitle: "",
        frontText: t("Enterprise-grade SAML SSO"),
        boldText: "",
        backText: "",
      },
      {
        key: "enterpriseText8",
        subTitle: "",
        frontText: t("SCORM Export"),
        boldText: "",
        backText: "",
      },
      {
        key: "enterpriseText9",
        subTitle: "",
        frontText: t("Bulk Video Generation"),
        boldText: "",
        backText: "",
      },
      {
        key: "enterpriseText10",
        subTitle: "",
        frontText: t("Video Production Support"),
        boldText: "",
        backText: "",
      },
      {
        key: "enterpriseText11",
        subTitle: "",
        frontText: t("24/7 Priority Technical Support"),
        boldText: "",
        backText: "",
      },
      {
        key: "enterpriseText12",
        subTitle: "",
        frontText: t("Dedicated Account Manager"),
        boldText: "",
        backText: "",
      },
    ];
  }, [t]);

  return (
    <StyledCellExplanationContainer>
      {plan === "personal" && (
        <div>
          {personalPlanStrings.map((stringObj) => {
            return <ExplanationLine {...stringObj} key={stringObj.key} />;
          })}
        </div>
      )}
      {plan === "team" && (
        <div>
          {teamPlanStrings.map((stringObj) => {
            return <ExplanationLine {...stringObj} key={stringObj.key} />;
          })}
        </div>
      )}
      {plan === "enterprise" && (
        <div>
          {enterprisePlanStrings.map((stringObj) => {
            return <ExplanationLine {...stringObj} key={stringObj.key} />;
          })}
        </div>
      )}
    </StyledCellExplanationContainer>
  );
};
interface SubscriptionListProps {
  period: boolean;
  plans: PlanType[];
  startSubscription: (plan: string, period: boolean) => void;
  upgradeSubscription: (plan: string, period: boolean) => void;
  downgradeSubscription: (plan: string, value: boolean) => void;
}
const SubscriptionList = ({
  period, // monthly 면 true, 아니면 false
  plans,
  startSubscription,
  upgradeSubscription,
  downgradeSubscription,
}: SubscriptionListProps) => {
  return (
    <StyledListBodyDiv>
      <FreeCell />
      <PersonalCell
        period={period}
        plans={plans}
        startSubscription={startSubscription}
        upgradeSubscription={upgradeSubscription}
        downgradeSubscription={downgradeSubscription}
      />
      <TeamCell
        period={period}
        plans={plans}
        startSubscription={startSubscription}
        upgradeSubscription={upgradeSubscription}
        downgradeSubscription={downgradeSubscription}
      />
      <EnterpriseCell />
    </StyledListBodyDiv>
  );
};

export interface SubscribeListModalProps {
  startSubscription: (plan: string, period: boolean, seats?: number) => void;
  upgradeSubscription: (plan: string, period: boolean, seats?: number) => void;
  downgradeSubscription: (
    plan: string,
    period: boolean,
    seats?: number
  ) => void;
}

export const SubscriptionListModal = (props: SubscribeListModalProps) => {
  const { startSubscription, upgradeSubscription, downgradeSubscription } =
    props;
  const router = useRouter();
  const { period: userPeriod } = router.query;
  const [period, setPeriod] = useState(userPeriod === "monthly" ? true : false);
  const { data: userInfo } = useGetUserInfoQuery(
    {},
    {
      pollingInterval: 30 * 60 * 1000,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
      skip: false,
    }
  );
  const { data: plans } = useGetPlansQuery({ product: "studio3.0" });

  const openedModal = useAppSelector(
    (store) => getProjectState(store).openedModal
  );

  const { setOpenedModal } = projectActions;
  const dispatch = useAppDispatch();

  const closeModal = () => {
    if (
      router.query?.openSubscribe === "true" ||
      router.query?.openCheckout === "true"
    ) {
      router.replace(window.location.pathname, undefined, {shallow: true});
    }

    if (userInfo?.interval === "month") {
      setPeriod(true);
    } else {
      setPeriod(false);
    }
    setTimeout(() => {
      dispatch(setOpenedModal(MODAL_NAME.NONE));
    }, 500)  
  };

  const changeToApiPlan = () => {
    if (
      router.query?.openSubscribe === "true" ||
      router.query?.openCheckout === "true"
    ) {
      router.replace(window.location.pathname, undefined, {shallow: true});
      setTimeout(() => {
        dispatch(setOpenedModal(MODAL_NAME.API_SUBSCRIPTION_LIST));
      }, 500)  
    } else dispatch(setOpenedModal(MODAL_NAME.API_SUBSCRIPTION_LIST));

  }


  // console.log("!@#!@#!@# ", { plans, userInfo });

  return (
    <StyledModal
      width={1240}
      centered
      open={openedModal === MODAL_NAME.SUBSCRIPTION_LIST}
      footer={null}
      onCancel={closeModal}
      zIndex={1002}
      destroyOnClose={true} 
    >
      <ModalHeader period={period} setPeriod={setPeriod} changeToApiPlan={changeToApiPlan}/>
      <SubscriptionList
        period={period}
        plans={plans}
        startSubscription={startSubscription}
        upgradeSubscription={upgradeSubscription}
        downgradeSubscription={downgradeSubscription}
      />
    </StyledModal>
  );
};
